// Müşteri Profili — plan §4.3.2 + design ref #screen-client-profile.
// /clients/:id — sol: şirket bilgileri. Sağ: proje kartları + "Yeni Proje".

import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  formatReportingPeriod,
  type Client,
  type Project,
  type ProjectsListResponse,
} from "@mahir/shared";
import { api } from "../api/client";
import { listClientEntities } from "../api/client-entities";
import { ClientAdminsCard } from "../components/clients/ClientAdminsCard";
import { ClientEvidencePolicyCard } from "../components/clients/ClientEvidencePolicyCard";
import { ClientKpiAccessCard } from "../components/clients/ClientKpiAccessCard";
import { ClientReportingCalendarCard } from "../components/clients/ClientReportingCalendarCard";
import { ClientTabs } from "../components/clients/ClientTabs";
import { useBreadcrumbDetail } from "../hooks/use-breadcrumb-detail";
import { useAuthStore } from "../stores/auth-store";

export function ClientProfile(): JSX.Element {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canCreateProject = user?.permissions["projects.create"] === true;
  const canViewKpi = user?.permissions["kpi.view"] === true;

  const [client, setClient] = useState<Client | null>(null);
  const [projects, setProjects] = useState<Project[] | null>(null);
  // M16 T17 — İşletmeler sekmesi sayacı (hata sessiz: sekme sayısız görünür)
  const [entityCount, setEntityCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Finar rev-A 15 — breadcrumb "Müşteriler / <müşteri adı>" ("Detay" yerine)
  useBreadcrumbDetail(client?.name);

  useEffect(() => {
    if (!id) return;
    let aborted = false;
    setLoading(true);
    Promise.all([
      api<{ client: Client }>(`/clients/${id}`),
      api<ProjectsListResponse>(`/projects?clientId=${id}`).catch(() => ({
        projects: [] as Project[],
        metrics: { total: 0, draft: 0, inProgress: 0, completed: 0 },
      })),
      listClientEntities(id).catch(() => null),
    ])
      .then(([c, p, entities]) => {
        if (!aborted) {
          setClient(c.client);
          setProjects(p.projects);
          setEntityCount(entities ? entities.length : null);
          setError(null);
        }
      })
      .catch((err) => {
        if (!aborted) setError(err instanceof Error ? err.message : "Müşteri yüklenemedi");
      })
      .finally(() => {
        if (!aborted) setLoading(false);
      });
    return () => {
      aborted = true;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="p-8" style={{ color: "var(--color-text-2)" }}>
        Yükleniyor…
      </div>
    );
  }

  if (error || !client) {
    return (
      <div className="p-8">
        <div
          className="max-w-2xl px-4 py-3 rounded-lg text-sm"
          style={{
            background: "color-mix(in srgb, var(--color-error) 10%, white)",
            color: "var(--color-error)",
          }}
        >
          {error ?? "Müşteri bulunamadı"}
        </div>
        <Link
          to="/clients"
          className="inline-block mt-4 text-sm"
          style={{ color: "var(--color-primary)" }}
        >
          ← Müşteri listesine dön
        </Link>
      </div>
    );
  }

  const avatarText = client.name
    .split(" ")
    .map((s) => s[0] ?? "")
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="p-8" style={{ fontFamily: "var(--font-sans)", maxWidth: 1200 }}>
      <Link to="/clients" className="text-sm" style={{ color: "var(--color-primary)" }}>
        ← Müşteri listesine dön
      </Link>

      {/* Header */}
      <header className="mt-4 mb-8 flex items-center gap-5">
        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: 16,
            background: "var(--color-primary)",
            color: "var(--color-surface)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.4rem",
            fontWeight: 700,
            fontFamily: "var(--font-display)",
            flexShrink: 0,
          }}
        >
          {avatarText}
        </div>
        <div className="flex-1 min-w-0">
          <h1
            className="text-3xl"
            style={{ fontFamily: "var(--font-display)", color: "var(--color-text)" }}
          >
            {client.name}
          </h1>
          <div
            className="mt-1 flex gap-3 flex-wrap text-sm"
            style={{ color: "var(--color-text-2)" }}
          >
            {client.sector ? <span>{client.sector}</span> : null}
            {client.country ? (
              <>
                <span style={{ color: "var(--color-text-3)" }}>·</span>
                <span>{client.country}</span>
              </>
            ) : null}
            {client.size ? (
              <>
                <span style={{ color: "var(--color-text-3)" }}>·</span>
                <span>{client.size}</span>
              </>
            ) : null}
            {/* Finar rev-A 15 — kısa kimlik (#2c4ab035) kullanıcıya anlam taşımadığı için gösterilmez */}
          </div>
        </div>
        {canViewKpi ? (
          <button
            onClick={() => void navigate(`/clients/kpi/ozet?clientId=${client.id}`)}
            className="px-4 py-2.5 rounded-lg font-semibold text-sm"
            style={{ border: "1px solid var(--color-border)", color: "var(--color-primary)" }}
          >
            Performans Göstergeleri
          </button>
        ) : null}
        {canCreateProject ? (
          <button
            onClick={() => void navigate(`/projects/new?clientId=${client.id}`)}
            className="px-4 py-2.5 rounded-lg font-semibold text-sm"
            style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
          >
            + Yeni Proje
          </button>
        ) : null}
      </header>

      {/* M16 DR5 — sekme çubuğu; Profil sekmesi bilgi kartları + projeleri birlikte gösterir (RK15;
          Finar rev-A 14: ayrı "Projeler (n)" sekmesi kalktı — Profil'deki listeye çapaydı) */}
      <ClientTabs
        clientId={client.id}
        active="profile"
        entityCount={entityCount}
        {...(canViewKpi ? { kpiHref: `/clients/kpi/ozet?clientId=${client.id}` } : {})}
      />

      <div className="grid gap-6" style={{ gridTemplateColumns: "320px 1fr" }}>
        {/* Sol: bilgi panelleri */}
        <aside className="flex flex-col gap-4">
          <InfoCard title="Şirket Bilgileri">
            <InfoRow label="Sektör" value={client.sector} />
            <InfoRow label="Ülke" value={client.country} />
            <InfoRow label="Büyüklük" value={client.size} />
            <InfoRow label="Sorumlu Danışman" value={client.createdByName} />
            <InfoRow
              label="Oluşturulma"
              value={new Date(client.createdAt).toLocaleDateString("tr-TR", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            />
          </InfoCard>

          {client.primaryContactName || client.primaryContactEmail || client.primaryContactPhone ? (
            <InfoCard title="İletişim Kişisi">
              <InfoRow label="Ad Soyad" value={client.primaryContactName} />
              <InfoRow label="E-posta" value={client.primaryContactEmail} mono />
              <InfoRow label="Telefon" value={client.primaryContactPhone} mono />
            </InfoCard>
          ) : null}

          {/* M16 T20 — müşteri yöneticileri (client_admins, Δ1); izinsizde render edilmez */}
          <ClientAdminsCard clientId={client.id} />
          {/* TODO-EVIDENCE-03 — kanıt saklama/gizlilik override'ı (clients.edit_all; izinsizde render edilmez) */}
          <ClientEvidencePolicyCard clientId={client.id} />
          {/* M17 T5 — mali yıl başlangıç ayı (düzenleme clients.edit_all; KPI değeri varken kilitli) */}
          <ClientReportingCalendarCard clientId={client.id} />
        </aside>

        {/* Orta: projeler + KPI erişimi (TODO-KPI-19 — izinsizde render edilmez) */}
        <section id="projeler" className="flex flex-col gap-6">
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2
                className="text-lg"
                style={{ fontFamily: "var(--font-display)", color: "var(--color-text)" }}
              >
                Projeler{" "}
                <span style={{ color: "var(--color-text-3)", fontSize: "0.9rem", fontWeight: 400 }}>
                  ({projects?.length ?? 0})
                </span>
              </h2>
            </div>
            {projects && projects.length > 0 ? (
              <div
                className="grid gap-3"
                style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}
              >
                {projects.map((p) => (
                  <ProjectCard key={p.id} project={p} />
                ))}
              </div>
            ) : (
              <div
                className="p-6 text-center rounded-lg"
                style={{
                  background: "var(--color-surface)",
                  border: "1px dashed var(--color-border)",
                  color: "var(--color-text-3)",
                }}
              >
                Henüz proje yok.
                {canCreateProject ? (
                  <div className="mt-3">
                    <button
                      onClick={() => void navigate(`/projects/new?clientId=${client.id}`)}
                      className="px-3 py-1.5 rounded text-sm"
                      style={{
                        background: "var(--color-primary)",
                        color: "var(--color-surface)",
                      }}
                    >
                      İlk projeyi oluştur
                    </button>
                  </div>
                ) : null}
              </div>
            )}
          </div>
          <ClientKpiAccessCard clientId={client.id} sector={client.sector} />
        </section>
      </div>
    </div>
  );
}

function InfoCard({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <div
      className="rounded-xl p-5"
      style={{
        background: "var(--color-surface)",
        border: "1px solid var(--color-border)",
      }}
    >
      <h3
        className="mb-3"
        style={{
          fontFamily: "var(--font-display)",
          fontSize: "0.95rem",
          color: "var(--color-text)",
        }}
      >
        {title}
      </h3>
      <div className="grid gap-2.5">{children}</div>
    </div>
  );
}

function InfoRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
}): JSX.Element {
  return (
    <div className="grid gap-1" style={{ gridTemplateColumns: "100px 1fr" }}>
      <span style={{ color: "var(--color-text-3)", fontSize: "0.78rem" }}>{label}</span>
      <span
        style={{
          color: value ? "var(--color-text)" : "var(--color-text-3)",
          fontSize: "0.85rem",
          fontFamily: mono ? "var(--font-mono)" : "var(--font-sans)",
          wordBreak: "break-word",
        }}
      >
        {value ?? "—"}
      </span>
    </div>
  );
}

function ProjectCard({ project }: { project: Project }): JSX.Element {
  const navigate = useNavigate();
  const accent = frameworkAccent(project.frameworkName);
  const statusLabel =
    project.status === "draft"
      ? "Taslak"
      : project.status === "in_progress"
        ? "Devam ediyor"
        : "Tamamlandı";

  return (
    <button
      onClick={() => void navigate(`/projects/${project.id}`)}
      className="text-left rounded-xl p-4 transition-transform"
      style={{
        background: "var(--color-surface)",
        border: "1px solid var(--color-border)",
        cursor: "pointer",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "var(--shadow-md)")}
      onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "none")}
    >
      <div className="flex items-center gap-2 mb-2">
        <span
          style={{
            fontSize: "0.65rem",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            padding: "2px 8px",
            borderRadius: 999,
            background: accent.bg,
            color: accent.fg,
            fontWeight: 600,
          }}
        >
          {project.frameworkName}
        </span>
        <span
          style={{
            fontSize: "0.72rem",
            color: "var(--color-text-3)",
            fontFamily: "var(--font-mono)",
          }}
        >
          {project.versionNumber} ·{" "}
          {formatReportingPeriod(project.reportingYear, project.periodType)}
        </span>
      </div>
      <div style={{ color: "var(--color-text)", fontSize: "0.95rem", fontWeight: 500 }}>
        {project.frameworkName} · {formatReportingPeriod(project.reportingYear, project.periodType)}
      </div>
      <div className="mt-2 text-xs flex justify-between" style={{ color: "var(--color-text-3)" }}>
        <span>{statusLabel}</span>
        <span>{project.assignedToName}</span>
      </div>
    </button>
  );
}

function frameworkAccent(name: string): { bg: string; fg: string } {
  const n = name.toLowerCase();
  if (n.includes("gri")) return { bg: "rgba(15,42,61,0.1)", fg: "var(--color-primary)" };
  if (n.includes("faaliyet") || n.includes("fa"))
    return {
      bg: "color-mix(in srgb, var(--color-warning) 15%, white)",
      fg: "var(--color-warning)",
    };
  if (n.includes("tsrs"))
    return {
      bg: "color-mix(in srgb, var(--color-success) 12%, white)",
      fg: "var(--color-success)",
    };
  return { bg: "rgba(15,42,61,0.06)", fg: "var(--color-text-2)" };
}
